import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import quentariLogo from "@/assets/quentari-logo.png";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { Menu } from "lucide-react";
import { MobileMenu } from "./MobileMenu";

const navItems = [
  { label: "Prozess", id: "process" },
  { label: "Leistungen", id: "features" },
  { label: "Ergebnisse", id: "outcomes" },
  { label: "FAQ", id: "faq" },
];

export const LandingNavbar = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Track scroll position and active section
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    setScrollY(currentScrollY);

    // Find active section based on scroll position
    const sections = navItems.map(item => ({
      id: item.id,
      element: document.getElementById(item.id)
    })).filter(s => s.element);

    const viewportMiddle = currentScrollY + window.innerHeight / 3;
    
    let current: string | null = null;
    for (const section of sections) {
      if (section.element) {
        const rect = section.element.getBoundingClientRect();
        const sectionTop = rect.top + currentScrollY;
        const sectionBottom = sectionTop + rect.height;
        
        if (viewportMiddle >= sectionTop && viewportMiddle < sectionBottom) {
          current = section.id;
          break;
        }
      }
    }
    
    setActiveSection(current);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Calculate dynamic styles based on scroll
  const scrollProgress = Math.min(scrollY / 100, 1);
  const isScrolled = scrollY > 20;

  return (
    <>
      <nav
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out"
        )}
        style={{
          backgroundColor: isScrolled 
            ? `hsl(var(--background) / ${0.7 + scrollProgress * 0.2})` 
            : 'transparent',
          backdropFilter: isScrolled ? `blur(${8 + scrollProgress * 8}px)` : 'none',
          boxShadow: isScrolled 
            ? `0 1px 3px hsl(var(--foreground) / ${scrollProgress * 0.05}), 0 4px 12px hsl(var(--foreground) / ${scrollProgress * 0.03})` 
            : 'none',
          borderBottom: isScrolled 
            ? `1px solid hsl(var(--border) / ${scrollProgress * 0.5})` 
            : '1px solid transparent',
        }}
      >
        <div className="container max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex h-16 md:h-20 items-center justify-between">
            {/* Logo */}
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg group"
              aria-label="Zurück nach oben scrollen"
            >
              <img 
                src={quentariLogo} 
                alt="Quentari" 
                width={128}
                height={32}
                className="h-7 md:h-8 w-auto transition-all duration-300 group-hover:opacity-80" 
              />
            </button>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={cn(
                      "relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg group",
                      isActive 
                        ? "text-primary" 
                        : "text-foreground/70 hover:text-foreground"
                    )}
                  >
                    {/* Background highlight */}
                    <span 
                      className={cn(
                        "absolute inset-0 rounded-lg transition-all duration-300",
                        isActive 
                          ? "bg-primary/8" 
                          : "bg-transparent group-hover:bg-muted/50"
                      )}
                    />
                    
                    {/* Label */}
                    <span className="relative z-10">{item.label}</span>
                    
                    {/* Active indicator dot */}
                    <span 
                      className={cn(
                        "absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary transition-all duration-300",
                        isActive 
                          ? "opacity-100 scale-100" 
                          : "opacity-0 scale-0"
                      )}
                    />
                  </button>
                );
              })}
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-3">
              {user ? (
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="h-10 px-6 font-medium hidden md:inline-flex transition-all duration-300 hover:shadow-md hover:shadow-primary/20"
                >
                  Zum Dashboard
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/auth')}
                    className="h-10 px-4 font-medium hidden md:inline-flex hover:bg-muted/50 transition-all duration-300"
                  >
                    Anmelden
                  </Button>
                  <Button
                    onClick={() => window.location.href = 'mailto:info@quentari.com?subject=Pilot%20Commitment'}
                    className="h-10 px-6 font-medium hidden md:inline-flex transition-all duration-300 hover:shadow-lg hover:shadow-primary/25 hover:-translate-y-0.5"
                  >
                    Pilot starten
                  </Button>
                </>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="md:hidden p-2 rounded-lg hover:bg-muted/50 transition-all duration-300 active:scale-95"
                aria-label="Menü öffnen"
              >
                <Menu className="w-6 h-6 text-foreground" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onNavigate={scrollToSection}
        user={user}
        onAuthClick={() => navigate('/auth')}
        onDashboardClick={() => navigate('/dashboard')}
      />
    </>
  );
};
